import { useSnackbar } from 'notistack'
import { useEffect, useMemo } from 'react'
import { useVehiclesQuery } from 'src/api/queries'
import ContentLoading from 'src/components/ContentLoading'
import TitledPage from 'src/components/layout/TitledPage'
import useResponsive from 'src/hooks/useResponsive'
import { Map } from './parts'

// ----------------------------------------------------------------------
// The fleet map is a top bar and a map, and nothing else.
//
// It is the one screen in the console whose content is a single object sized
// to the window, so everything the page frame normally contributes is
// subtracted here rather than worked around:
//
// **No head band.** `drawHeader={false}` publishes the name to the bar and
// draws no head. The band cost 76px of a map already sized to the viewport,
// which pushed the legend pinned to the map's bottom edge below the fold: a
// control that is always visible by design, reachable only by scrolling.
//
// **No bottom inset**, for the same reason: the map ends where the window
// ends, so the page's 80px tail was 80px of scroll under a full-height map.
//
// **The gutter stays, and the map negates it.** This page used to zero the
// container's own padding, which is why its heading sat 24px left of every
// other page's and flush against the nav rail. The gutter is the page's and
// the bleed belongs to the thing that bleeds; see `Map`.
// ----------------------------------------------------------------------

const VehicleMapPage = () => {
  const isDesktop = useResponsive('up', 'md')
  const { enqueueSnackbar } = useSnackbar()
  const { vehicles, loading, error, refetch } = useVehiclesQuery()

  const mapVehicles = useMemo(() => vehicles || [], [vehicles])

  useEffect(() => {
    if (!loading && error) {
      enqueueSnackbar(`Failed to load vehicles. Error: ${error?.message}`, {
        variant: 'error',
      })
    }
  }, [loading, error, enqueueSnackbar])

  return (
    <TitledPage
      title="Map"
      header="Map"
      drawHeader={false}
      meta={
        !isDesktop && (
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
          />
        )
      }
      containerMaxWidth={false}
      containerProps={{ fixed: false, sx: { pb: 0 } }}
    >
      {loading && <ContentLoading />}

      <Map
        vehicles={mapVehicles}
        fleetUnavailable={!loading && !!error}
        onRefresh={refetch}
      />
    </TitledPage>
  )
}

export default VehicleMapPage
